/**
 * Shared modal primitive.
 *
 * - Centered overlay with backdrop (bg-foreground/40).
 * - Closes on ESC keydown or backdrop click via onClose callback.
 * - Renders children inside a white card panel.
 * - Mounts via createPortal into document.body so the fixed overlay isn't
 *   affected by any ancestor's overflow/transform/contain containing block.
 *
 * Usage:
 *   <Modal onClose={fn} aria-label="Edit item">
 *     <h2>…</h2>
 *     …
 *   </Modal>
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  onClose: () => void;
  children: React.ReactNode;
  'aria-label'?: string;
  /** Maximum width of the content card. Defaults to "max-w-lg". */
  maxWidth?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  onClose,
  children,
  'aria-label': ariaLabel,
  maxWidth = 'max-w-lg',
}: ModalProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Focus management: move focus into the panel on mount, restore it to the
  // previously focused element on unmount.
  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    return () => {
      previouslyFocused?.focus();
    };
  }, []);

  // Close on ESC + trap Tab within the panel.
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === backdropRef.current) onClose();
  }

  const content = (
    <div
      ref={backdropRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4"
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`w-full ${maxWidth} max-h-[90vh] overflow-y-auto rounded-2xl bg-surface ring-1 ring-border elevate focus:outline-none`}
      >
        {children}
      </div>
    </div>
  );

  // Portal to document.body so fixed positioning works correctly even when
  // ancestors have overflow, transform, or contain properties that would
  // otherwise create a new CSS containing block for position:fixed.
  return createPortal(content, document.body);
}
